import { z } from 'zod/v3';

// ============================================================================
// 공통 Enum 스키마
// ============================================================================

/** 상품 문의 상태 (대기, 답변 완료, 삭제) */
export const productInquiryStatusSchema = z.enum([
  'PENDING',
  'ANSWERED',
  'DELETED',
]);
export type ProductInquiryStatus = z.infer<typeof productInquiryStatusSchema>;

/** 상품 문의 타입 */
export const productInquiryTypeSchema = z.enum([
  'PRODUCT',
  'DELIVERY',
  'RESTOCK',
  'ETC',
]);
export type ProductInquiryType = z.infer<typeof productInquiryTypeSchema>;

/** 커서 페이징 정렬 타입 */
export const cursorSortTypeSchema = z.enum([
  'SCORE',
  'DISPLAY_RANK',
  'LOW_PRICE',
  'LATEST_PRODUCTS',
]);
export type CursorSortType = z.infer<typeof cursorSortTypeSchema>;

// ============================================================================
// Model 스키마
// ============================================================================

/**
 * 상품 문의 응답 스키마
 * @see GET /api/market-place/v1/product-inquiries
 */
export const productInquirySchema = z.object({
  /** 상품 문의 답변 */
  answer: z.string().optional(),
  /** 구매자 ID */
  buyerId: z.number().optional(),
  /** 상품 문의 내용 */
  content: z.string(),
  /** 생성일(SKU 기준), ISO-8601 형식 문자열, UTC(+0) */
  createdAt: z.string().optional(),
  /** 상품 문의 ID */
  id: z.number().optional(),
  /** 비밀글 여부 */
  isSecret: z.boolean(),
  /** 상품ID(OFFER) */
  offerId: z.number().optional(),
  /** 상품 문의 답변 날짜, ISO-8601 형식 문자열, UTC(+0) */
  processedAt: z.string().optional(),
  /** 대표이미지 */
  productMainImages: z.array(z.string()),
  /** 상품명 */
  productName: z.string().optional(),
  /** 비밀글 여부 (deprecated) */
  secret: z.boolean().optional(),
  /** 상품 응답 상태(대기, 답변 완료) */
  status: productInquiryStatusSchema,
  /** 상품 문의 타입 */
  type: productInquiryTypeSchema,
  /** 수정일(SKU 기준), ISO-8601 형식 문자열, UTC(+0) */
  updatedAt: z.string().optional(),
  /** 입점사 ID */
  vendorId: z.number().optional(),
  /** 입점사 상호명 */
  vendorName: z.string(),
});
export type ProductInquiry = z.infer<typeof productInquirySchema>;

/**
 * 상품 문의 페이지(커서 페이징) 응답 스키마
 * @description CursorPagedResponse의 content를 상품 문의 배열로 구체화
 * @see GET /api/market-place/v1/product-inquiries
 */
export const productInquiryPageSchema = z.object({
  /** 응답 정보 (상품 문의 목록) */
  content: z.array(productInquirySchema),
  /** 다음 페이지 여부 */
  hasNextPage: z.boolean(),
  /** 마지막 항목 ID(다음페이지 요청시 필요) */
  lastId: z.number().optional(),
  /** pagination value */
  searchAfter: z.array(z.string()).optional(),
  /** 조회된 응답 수 */
  size: z.number(),
  /** 정렬 타입 */
  sortType: cursorSortTypeSchema.optional(),
});
export type ProductInquiryPage = z.infer<typeof productInquiryPageSchema>;
